import { inject, Injectable } from '@angular/core';
import {
  BasinStyle,
  ColumnConfiguration,
  HandleConfiguration,
  HandleType,
  RowConfiguration,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-validation.models';
import {
  BathroomCompositionDetailDto,
  CompositionColumnDto,
  CompositionRowDto,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-composition-dtos';
import { SearchBathroomProductDto } from 'src/app/models/configurators-2d/common-bathroom-dtos';
import {
  BathroomProductDto,
  InstallationType,
  ProductAttribute,
} from 'src/app/models/configurators-2d/tunnel/bathroom/bathroom-tunnel-dtos';
import { BathroomService } from '../../applications/bathroom.service';

@Injectable({
  providedIn: 'root',
})
export class BathroomProductService {
  private readonly bathroomService = inject(BathroomService);

  // ============================================
  // Recherche de produits
  // ============================================

  /**
   * Recherche un produit vasque (plan vasque ou vasque à poser)
   */
  async searchBasinProduct(basinStyle: BasinStyle, width: number): Promise<BathroomProductDto[]> {
    const productAttribute =
      basinStyle === BasinStyle.WashbasinTop ? ProductAttribute.WashbasinTop : ProductAttribute.CountertopBasin;

    const searchCriteria: SearchBathroomProductDto = {
      productAttribute,
      width: basinStyle === BasinStyle.WashbasinTop ? width : undefined,
    };

    return this.bathroomService.searchProducts(searchCriteria);
  }

  /**
   * Recherche un produit plan de toilette (pour vasque à poser)
   */
  async searchCoverProduct(finishingId: string | undefined, width: number): Promise<BathroomProductDto[]> {
    const searchCriteria: SearchBathroomProductDto = {
      bathroomFinishingId: finishingId,
      productAttribute: ProductAttribute.ToiletTop,
      width,
    };

    return this.bathroomService.searchProducts(searchCriteria);
  }

  /**
   * Recherche un produit colonne (tiroir/niche)
   */
  async searchColumnProduct(
    columnConfig: ColumnConfiguration,
    criteria: Partial<SearchBathroomProductDto>
  ): Promise<BathroomProductDto[]> {
    const searchCriteria: SearchBathroomProductDto = {
      productAttribute: columnConfig.currentProduct?.attribute,
      width: criteria.width || columnConfig.width,
      bathroomFinishingId: criteria.bathroomFinishingId || columnConfig.finitionId,
      heightThickness: 30,
      installationType: criteria.installationType ?? columnConfig.currentProduct?.installationType,
    };

    return this.bathroomService.searchProducts(searchCriteria);
  }

  /**
   * Recherche un produit colonne (tiroir) avec un installationType spécifique
   */
  async searchColumnProductWithInstallationType(
    column: CompositionColumnDto,
    installationType: InstallationType
  ): Promise<BathroomProductDto[]> {
    if (!column.product) return [];

    const searchCriteria: SearchBathroomProductDto = {
      productAttribute: column.product.attribute,
      width: column.width,
      bathroomFinishingId: column.product.bathroomFinishingId,
      heightThickness: column.product.heightThickness,
      installationType,
    };

    return this.bathroomService.searchProducts(searchCriteria);
  }

  /**
   * Recherche des poignées
   */
  async searchHandleProduct(handleConfig: HandleConfiguration): Promise<BathroomProductDto[]> {
    const searchCriteria: SearchBathroomProductDto = {
      width: handleConfig.handleLength,
      productAttribute: ProductAttribute.Handles,
      bathroomFinishingId: handleConfig.handleFinitionId,
    };

    return this.bathroomService.searchProducts(searchCriteria);
  }

  // ============================================
  // Calcul des dimensions de tiroirs
  // ============================================

  /**
   * Calcule la largeur effective d'un tiroir en fonction de la configuration
   * - Si niche à côté : la largeur est celle de la rangée moins la largeur de la niche
   * - Si deux tiroirs côte à côte : la largeur est divisée par 2
   */
  calculateDrawerWidth(row: CompositionRowDto, column: CompositionColumnDto, rowTotalWidth: number): number {
    // Si c'est une niche ou un élément fixe, retourner sa largeur fixe
    if (column.product?.attribute === ProductAttribute.Niche || column.hasElementFix) {
      return column.width;
    }

    // Les colonnes à taille fixe : niches et éléments avec hasElementFix
    const fixedColumns = row.columns.filter(c => c.product?.attribute === ProductAttribute.Niche || c.hasElementFix);
    const resizableColumns = row.columns.filter(
      c => c.product?.attribute !== ProductAttribute.Niche && !c.hasElementFix
    );

    // Soustraire la largeur des éléments fixes de la largeur totale
    const fixedWidth = fixedColumns.reduce((sum, c) => sum + c.width, 0);
    const availableWidth = rowTotalWidth - fixedWidth;

    // Si plusieurs tiroirs redimensionnables côte à côte, diviser équitablement
    if (resizableColumns.length >= 2) {
      return availableWidth / resizableColumns.length;
    }

    return availableWidth;
  }

  /**
   * Met à jour tous les produits tiroir d'une rangée avec une nouvelle largeur totale
   */
  async updateRowDrawerProducts(
    composition: BathroomCompositionDetailDto,
    rowIndex: number,
    newTotalWidth: number
  ): Promise<BathroomCompositionDetailDto> {
    if (!composition.compositionConfiguration) return composition;

    const row = composition.compositionConfiguration.rows.find(r => r.index === rowIndex);
    if (!row) return composition;

    let updatedComposition = composition;

    for (const column of row.columns) {
      // Ne pas modifier les niches et les éléments fixes (largeur fixe)
      if (column.product?.attribute === ProductAttribute.Niche || column.hasElementFix) continue;

      // Calculer la nouvelle largeur pour ce tiroir
      const drawerWidth = this.calculateDrawerWidth(row, column, newTotalWidth);

      // Rechercher un produit avec la nouvelle largeur
      const columnConfig: ColumnConfiguration = {
        columnId: column.id,
        columnIndex: column.index,
        width: drawerWidth,
        finitionId: column.product?.bathroomFinishingId,
        availableFinitions: [],
        currentProductId: column.productId,
        currentProduct: column.product,
      };

      const products = await this.searchColumnProduct(columnConfig, { width: drawerWidth });

      if (products.length > 0) {
        updatedComposition = this.applyProductToColumn(updatedComposition, rowIndex, column.index, products[0]);
      }
    }

    return updatedComposition;
  }

  // ============================================
  // Application des produits (fonctions pures)
  // ============================================

  /**
   * Applique un produit à une colonne et retourne la composition modifiée
   */
  applyProductToColumn(
    composition: BathroomCompositionDetailDto,
    rowIndex: number,
    columnIndex: number,
    product: BathroomProductDto
  ): BathroomCompositionDetailDto {
    if (!composition.compositionConfiguration) return composition;

    const updatedRows = composition.compositionConfiguration.rows.map(row => {
      if (row.index !== rowIndex) return row;
      return {
        ...row,
        columns: row.columns.map(col =>
          col.index === columnIndex ? { ...col, productId: product.id, product, width: product.width } : col
        ),
      };
    });

    return {
      ...composition,
      compositionConfiguration: {
        ...composition.compositionConfiguration,
        rows: updatedRows,
      },
    };
  }

  /**
   * Applique un produit plan de toilette (cover) et retourne la composition modifiée
   */
  applyProductToCover(
    composition: BathroomCompositionDetailDto,
    rowIndex: number,
    product: BathroomProductDto
  ): BathroomCompositionDetailDto {
    if (!composition.compositionConfiguration) return composition;

    const updatedRows = composition.compositionConfiguration.rows.map(row =>
      row.index === rowIndex ? { ...row, coverProductId: product.id, coverProduct: product } : row
    );

    return {
      ...composition,
      compositionConfiguration: {
        ...composition.compositionConfiguration,
        rows: updatedRows,
      },
    };
  }

  /**
   * Applique un produit vasque (basin) et retourne la composition modifiée
   */
  applyProductToBasin(
    composition: BathroomCompositionDetailDto,
    rowIndex: number,
    product: BathroomProductDto
  ): BathroomCompositionDetailDto {
    if (!composition.compositionConfiguration) return composition;

    let updatedRows: CompositionRowDto[];

    if (product.attribute === ProductAttribute.WashbasinTop) {
      // Plan vasque : supprimer le coverProduct
      updatedRows = composition.compositionConfiguration.rows.map(row =>
        row.index === rowIndex
          ? {
              ...row,
              basinProductId: product.id,
              basinProduct: product,
              coverProduct: null as any,
              coverProductId: null as any,
            }
          : row
      );
    } else {
      // Vasque à poser : garder le coverProduct
      updatedRows = composition.compositionConfiguration.rows.map(row =>
        row.index === rowIndex ? { ...row, basinProductId: product.id, basinProduct: product } : row
      );
    }

    return {
      ...composition,
      compositionConfiguration: {
        ...composition.compositionConfiguration,
        rows: updatedRows,
      },
    };
  }

  /**
   * Applique une poignée à une colonne et retourne la composition modifiée
   */
  applyHandleToColumn(
    composition: BathroomCompositionDetailDto,
    rowIndex: number,
    columnIndex: number,
    handle: BathroomProductDto
  ): BathroomCompositionDetailDto {
    if (!composition.compositionConfiguration) return composition;

    const updatedRows = composition.compositionConfiguration.rows.map(row => {
      if (row.index !== rowIndex) return row;
      return {
        ...row,
        columns: row.columns.map(col => (col.index === columnIndex ? { ...col, handleId: handle.id, handle } : col)),
      };
    });

    return {
      ...composition,
      compositionConfiguration: {
        ...composition.compositionConfiguration,
        rows: updatedRows,
      },
    };
  }

  /**
   * Supprime le coverProduct d'une ligne et retourne la composition modifiée
   */
  removeCoverProduct(composition: BathroomCompositionDetailDto, rowIndex: number): BathroomCompositionDetailDto {
    if (!composition.compositionConfiguration) return composition;

    const updatedRows = composition.compositionConfiguration.rows.map(row =>
      row.index === rowIndex ? { ...row, coverProductId: undefined, coverProduct: undefined } : row
    );

    return {
      ...composition,
      compositionConfiguration: {
        ...composition.compositionConfiguration,
        rows: updatedRows,
      },
    };
  }

  // ============================================
  // Opérations combinées (recherche + application)
  // ============================================

  /**
   * Met à jour les produits lors d'un changement de style de vasque
   * Retourne la composition modifiée ou null si erreur
   */
  async updateBasinStyleProducts(
    composition: BathroomCompositionDetailDto,
    basinStyle: BasinStyle
  ): Promise<BathroomCompositionDetailDto | null> {
    if (!composition.compositionConfiguration) return null;

    const firstRow = composition.compositionConfiguration.rows.find(r => r.index === 0);
    if (!firstRow) return null;

    let updatedComposition = composition;

    if (basinStyle === BasinStyle.CountertopBasin) {
      // Ajouter un plan de toilette
      const finishingId = firstRow.columns[0]?.product?.bathroomFinishingId;

      try {
        const coverProducts = await this.searchCoverProduct(finishingId, firstRow.width);
        if (coverProducts.length > 0) {
          updatedComposition = this.applyProductToCover(updatedComposition, 0, coverProducts[0]);
        }

        // Ajouter une vasque à poser
        const basinProducts = await this.searchBasinProduct(BasinStyle.CountertopBasin, firstRow.width);
        if (basinProducts.length > 0) {
          updatedComposition = this.applyProductToBasin(updatedComposition, 0, basinProducts[0]);
        }
      } catch (error) {
        console.error('Erreur lors de la mise à jour des produits pour Vasque à poser', error);
        return null;
      }
    } else {
      // Plan vasque
      try {
        const planVasqueProducts = await this.searchBasinProduct(BasinStyle.WashbasinTop, firstRow.width);
        if (planVasqueProducts.length > 0) {
          updatedComposition = this.applyProductToBasin(updatedComposition, 0, planVasqueProducts[0]);
        }
        // Supprimer le coverProduct
        updatedComposition = this.removeCoverProduct(updatedComposition, 0);
      } catch (error) {
        console.error('Erreur lors de la mise à jour des produits pour Plan vasque', error);
        return null;
      }
    }

    return updatedComposition;
  }

  /**
   * Met à jour le plan de toilette ou plan vasque après un changement de largeur
   */
  async updateTopProductForNewWidth(
    composition: BathroomCompositionDetailDto,
    newWidth: number,
    basinStyle: BasinStyle
  ): Promise<BathroomCompositionDetailDto | null> {
    if (!composition.compositionConfiguration) return null;

    const firstRow = composition.compositionConfiguration.rows.find(r => r.index === 0);
    if (!firstRow) return null;

    let updatedComposition = composition;

    try {
      if (basinStyle === BasinStyle.CountertopBasin) {
        // Mettre à jour le plan de toilette
        // Utiliser la finition du coverProduct existant, sinon celle de la première colonne
        const finishingId =
          firstRow.coverProduct?.bathroomFinishingId ?? firstRow.columns[0]?.product?.bathroomFinishingId;
        const coverProducts = await this.searchCoverProduct(finishingId, newWidth);
        if (coverProducts.length > 0) {
          updatedComposition = this.applyProductToCover(updatedComposition, 0, coverProducts[0]);
        }
      } else {
        // Mettre à jour le plan vasque
        const planVasqueProducts = await this.searchBasinProduct(BasinStyle.WashbasinTop, newWidth);
        if (planVasqueProducts.length > 0) {
          updatedComposition = this.applyProductToBasin(updatedComposition, 0, planVasqueProducts[0]);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du produit top', error);
      return null;
    }

    return updatedComposition;
  }

  /**
   * Met à jour un produit colonne (recherche + application)
   */
  async updateColumnProduct(
    composition: BathroomCompositionDetailDto,
    rowConfigurations: RowConfiguration[],
    rowIndex: number,
    columnIndex: number,
    criteria: Partial<SearchBathroomProductDto>
  ): Promise<BathroomCompositionDetailDto | null> {
    const rowConfig = rowConfigurations.find(r => r.rowIndex === rowIndex);
    const columnConfig = rowConfig?.columnConfigurations.find(c => c.columnIndex === columnIndex);

    if (!columnConfig) return null;

    try {
      const products = await this.searchColumnProduct(columnConfig, criteria);
      if (products.length > 0) {
        return this.applyProductToColumn(composition, rowIndex, columnIndex, products[0]);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du produit colonne', error);
    }

    return null;
  }

  /**
   * Met à jour toutes les poignées et les tiroirs avec le bon installationType
   */
  async updateAllHandles(
    composition: BathroomCompositionDetailDto,
    handleConfig: HandleConfiguration
  ): Promise<BathroomCompositionDetailDto | null> {
    if (!composition.compositionConfiguration) return null;

    let updatedComposition = composition;

    // Déterminer le type d'installation correspondant au type de poignée
    const targetInstallationType =
      handleConfig.handleType === HandleType.Integrated
        ? InstallationType.WallMountedIntegratedHandle
        : InstallationType.WallMountedFixedHandle;

    try {
      // 1. Mettre à jour les tiroirs avec le bon installationType
      updatedComposition = await this.updateAllDrawersInstallationType(updatedComposition, targetInstallationType);

      // 2. Gérer les poignées selon le type
      if (handleConfig.handleType === HandleType.Integrated) {
        // Supprimer les poignées de toutes les colonnes
        updatedComposition = this.removeAllHandles(updatedComposition);
      } else {
        // HandleType.ToFix - Ajouter les poignées si les paramètres sont définis
        if (handleConfig.handleLength && handleConfig.handleFinitionId) {
          const handleProducts = await this.searchHandleProduct(handleConfig);
          if (handleProducts.length > 0) {
            for (const row of updatedComposition.compositionConfiguration!.rows) {
              for (const column of row.columns) {
                // N'ajouter des poignées que sur les tiroirs (pas sur les niches)
                if (column.product?.attribute === ProductAttribute.UnderWashbasin) {
                  updatedComposition = this.applyHandleToColumn(
                    updatedComposition,
                    row.index,
                    column.index,
                    handleProducts[0]
                  );
                }
              }
            }
          }
        }
      }

      return updatedComposition;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des poignées', error);
      return null;
    }
  }

  /**
   * Met à jour tous les tiroirs avec un nouveau type d'installation
   */
  private async updateAllDrawersInstallationType(
    composition: BathroomCompositionDetailDto,
    installationType: InstallationType
  ): Promise<BathroomCompositionDetailDto> {
    if (!composition.compositionConfiguration) return composition;

    let updatedComposition = composition;

    for (const row of composition.compositionConfiguration.rows) {
      for (const column of row.columns) {
        // Ne mettre à jour que les tiroirs (pas les niches)
        if (column.product?.attribute !== ProductAttribute.UnderWashbasin) continue;

        // Vérifier si le tiroir a déjà le bon installationType
        if (column.product.installationType === installationType) continue;

        // Rechercher un tiroir avec le bon installationType
        const products = await this.searchColumnProductWithInstallationType(column, installationType);

        if (products.length > 0) {
          updatedComposition = this.applyProductToColumn(updatedComposition, row.index, column.index, products[0]);
        }
      }
    }

    return updatedComposition;
  }

  /**
   * Supprime les poignées de toutes les colonnes
   */
  removeAllHandles(composition: BathroomCompositionDetailDto): BathroomCompositionDetailDto {
    if (!composition.compositionConfiguration) return composition;

    const updatedRows = composition.compositionConfiguration.rows.map(row => ({
      ...row,
      columns: row.columns.map(col => ({
        ...col,
        handleId: undefined,
        handle: undefined,
      })),
    }));

    return {
      ...composition,
      compositionConfiguration: {
        ...composition.compositionConfiguration,
        rows: updatedRows,
      },
    };
  }

  /**
   * Met à jour le produit plan de toilette (cover) avec une nouvelle finition
   */
  async updateCoverProduct(
    composition: BathroomCompositionDetailDto,
    rowIndex: number,
    finitionId: string
  ): Promise<BathroomCompositionDetailDto | null> {
    if (!composition.compositionConfiguration) return null;

    const row = composition.compositionConfiguration.rows.find(r => r.index === rowIndex);
    if (!row) return null;

    try {
      const products = await this.searchCoverProduct(finitionId, row.width);
      if (products.length > 0) {
        return this.applyProductToCover(composition, rowIndex, products[0]);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du plan de toilette', error);
    }

    return null;
  }
}
